<template>
  <v-skeleton-loader
    v-if="busy"
    :type="skeletonLoader"
    fill-height
  ></v-skeleton-loader>
  <div v-else>
    <v-list v-model:opened="openGroups" class="overflow-x-hidden">
      <v-card-text
        v-if="searchString && filteredHierarchy.length === 0"
        class="text-center pt-2 px-8 py-6"
      >
        <!-- No  matches your search term -->
        <div class="pb-3">
          <v-icon icon="mdi-emoticon-sad-outline" size="32" />
        </div>
        <div><strong>No services found</strong></div>
        <div>We couldn't find any services that matched your search term</div>
      </v-card-text>
      <v-list-group
        v-for="group in filteredHierarchy"
        :key="group.id"
        :value="group.id"
      >
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            v-model="group.expanded"
            :value="group.expanded"
            class="px-3 py-1"
            density="compact"
            tabindex="0"
            rounded="0"
          >
            <v-list-item-action>
              <v-layout style="display: flex; align-items: center">
                <v-checkbox
                  :disabled="
                    !group.selected && disableServicesSelection ? true : false
                  "
                  v-model="group.selected"
                  :indeterminate="isGroupIndeterminate(group)"
                  color="primary"
                  hide-details
                  @click.stop
                  @change="handleGroupSelection(group)"
                />
                <div class="d-flex align-center">
                  <span class="align-left group-name" :title="group.name">{{
                    group.name
                  }}</span>
                  <v-chip
                    v-if="getGroupTotal(group) > 0"
                    class="ml-2 mt-1 count-chip"
                    size="x-small"
                    density="compact"
                  >
                    {{ getGroupTotal(group) }}
                  </v-chip>
                </div>
              </v-layout>
            </v-list-item-action>
          </v-list-item>
        </template>

        <v-list-item
          v-for="service in group.metadata"
          :key="service.id"
          class="text-left pl-0"
          style="padding-left: 35px !important"
          density="compact"
          @click="
            !service.selected && disableServicesSelection
              ? null
              : toggleServiceSelection(group, service)
          "
        >
          <template v-slot:prepend>
            <v-icon :color="service.colour || 'primary'" class="service-icon">
              mdi-{{ service.icon }}
            </v-icon>
            <v-checkbox
              :disabled="
                !service.selected && disableServicesSelection ? true : false
              "
              v-model="service.selected"
              color="primary"
              hide-details
              class="ma-0 pa-0"
              style="margin-right: 4px"
            />
            <v-list-item-title
              class="align-left"
              style="
                padding-left: 0px !important;
                word-wrap: break-word;
                overflow-wrap: break-word;
                white-space: normal;
              "
              :style="
                getServiceCount(service.id) > 0
                  ? 'max-width: 55% !important'
                  : 'max-width: 70% !important'
              "
            >
              {{ service.service }}
              <v-chip
                v-if="getServiceCount(service.id) > 0"
                class="inline-chip"
                size="small"
                density="compact"
              >
                {{ getServiceCount(service.id) }}
              </v-chip>
              <!-- data will not appear on map warning -->
              <v-tooltip
                v-if="!service.plot_on_map && getServiceCount(service.id) > 0"
                text="This data cannot be shown on the map due to licensing restrictions."
              >
                <template #activator="{ props }">
                  <span v-bind="props">
                    <v-icon
                      class="ml-1"
                      aria-label="Warning: This data cannot be shown on the map due to licensing restrictions."
                      icon="mdi-alert"
                      color="#D97706"
                    />
                  </span>
                </template>
              </v-tooltip>
            </v-list-item-title>
          </template>
        </v-list-item>
      </v-list-group>
    </v-list>
  </div>
</template>

<script>
export default {
  name: "ServicesDirectoryList",
  data() {
    return {
      busy: false,
      openGroups: [],
    };
  },
  props: {
    searchString: {
      type: String,
      default: "",
    },
    disableServicesSelection: {
      type: Boolean,
      default: false,
    },
    serviceCounts: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {},
  computed: {
    serviceCountMap() {
      return this.serviceCounts || {};
    },
    skeletonLoader: {
      get() {
        var list = "";
        for (let i = 0; i < 10; i++) {
          list = "list-item-two-line," + list;
        }
        return list;
      },
    },
    selectedServices() {
      return this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy.reduce(
        (acc, group) => {
          const selectedInGroup = group.metadata.filter(
            (service) => service.selected,
          );
          return [...acc, ...selectedInGroup];
        },
        [],
      );
    },
    filteredHierarchy() {
      if (!this.searchString || this.searchString.trim() === "")
        return this.$store.state.servicesDirectory
          .servicesGroupsAndMetaHierarchy;

      const searchLower = this.searchString.toLowerCase();
      return this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy
        .map((group) => ({
          ...group,
          metadata: group.metadata.filter((service) =>
            (service.service?.toLowerCase() || "").includes(searchLower),
          ),
        }))
        .filter(
          (group) =>
            group.metadata.length > 0 ||
            group.name.toLowerCase().includes(searchLower),
        );
    },
  },
  mounted() {
    this.getServices();
    // Initial emit after component is mounted and if the this.$store.state.servicesDirectory.SDfetchedServices is empty
    // (if it's not then it's already done the intial call via the Areas tab)
    this.$nextTick(() => {
      if (
        Object.keys(this.$store.state.servicesDirectory.SDfetchedServices)
          .length === 0
      ) {
        this.$emit(
          "getServices",
          this.$store.state.servicesDirectory.SDselectedServices,
        );
      }
    });
  },
  methods: {
    getServices() {
      this.busy = true;

      // Only make AJAX call if data is null
      if (!this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy) {
        this.$axios
          .get("/sd-services-metadata-groups/hierarchy")
          .then((response) => {
            this.$store.commit(
              "servicesDirectory/setServicesGroupsAndMetaHierarchy",
              response.data,
            );
            this.processServicesData();
          })
          .catch((error) => {
            console.error("Error fetching services:", error);
          })
          .finally(() => {
            this.busy = false;
          });
      } else {
        // Data already exists, just process it
        this.processServicesData();
        this.busy = false;
      }
    },

    processServicesData() {
      // Get the current data from store
      const currentData =
        this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy;

      // Process the data with selection states
      this.$store.commit(
        "servicesDirectory/setServicesGroupsAndMetaHierarchy",
        currentData.map((group) => ({
          ...group,
          selected: false,
          metadata: group.metadata.map((service) => ({
            ...service,
            selected: false,
          })),
        })),
      );

      // After loading services, check the ones from URL
      this.$nextTick(() => {
        this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy.forEach(
          (group) => {
            group.metadata.forEach((service) => {
              if (
                this.$store.state.servicesDirectory.SDselectedServices.some(
                  (s) => s.id === service.id,
                )
              ) {
                service.selected = true;
              }
            });
            // Update group selection state
            this.handleServiceSelection(group);
          },
        );
      });
    },
    isGroupIndeterminate(group) {
      const selectedCount = group.metadata.filter(
        (service) => service.selected,
      ).length;
      return selectedCount > 0 && selectedCount < group.metadata.length;
    },
    handleGroupSelection(group) {
      const newState = group.selected;

      const storeGroup =
        this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy.find(
          (g) => g.id === group.id,
        );
      storeGroup.metadata.forEach((service) => {
        service.selected = newState;
        this.updateStoreSelection(service, newState);
      });
      storeGroup.selected = newState;
    },
    handleServiceSelection(group) {
      const allSelected = group.metadata.every((s) => s.selected);
      group.selected = allSelected;
    },
    toggleServiceSelection(group, service) {
      service.selected = !service.selected;
      this.updateStoreSelection(service, service.selected);
      this.handleServiceSelection(group);
    },
    updateStoreSelection(service, isSelected) {
      if (isSelected) {
        this.$store.dispatch("servicesDirectory/addSelectedService", {
          service,
          router: this.$router,
          route: this.$route,
        });
      } else {
        this.$store.dispatch("servicesDirectory/removeSelectedService", {
          service,
          router: this.$router,
          route: this.$route,
        });
      }
    },
    getServiceCount(categoryId) {
      return this.serviceCountMap[categoryId.toString()] || 0;
    },
    getGroupTotal(group) {
      return group.metadata.reduce((total, service) => {
        return total + (this.getServiceCount(service.id) || 0);
      }, 0);
    },
  },
  watch: {
    "$store.state.servicesDirectory.SDselectedServices": {
      handler(newVal, oldVal) {
        // Only emit if the services have actually changed
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$emit(
            "getServices",
            this.$store.state.servicesDirectory.SDselectedServices,
          );
        }
      },
      deep: true,
    },
    searchString: {
      handler(newVal) {
        if (newVal && newVal.trim() !== "") {
          // Add all group IDs to openGroups when searching
          this.openGroups = this.filteredHierarchy.map((group) => group.id);
        } else {
          // Clear openGroups when search is empty
          this.openGroups = [];
        }
      },
    },
    "$store.state.servicesDirectory.counts": {
      handler(newCounts) {
        if (newCounts?.serviceCounts) {
          // eslint-disable-next-line vue/no-mutating-props
          this.serviceCounts = newCounts.serviceCounts;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.service-list-item {
  padding: 0px !important;
  padding-left: 10px !important;
}

.service-icon {
  margin-right: 0px;
  opacity: 1 !important;
  filter: drop-shadow(1px 0px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(-1px 0px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(0px -1px 0px rgba(0, 0, 0, 0.12));
}

.inline-chip {
  display: inline-block !important;
  margin-left: 4px !important;
  margin-top: 1px !important;
  vertical-align: middle !important;
  white-space: nowrap !important;
}
.group-name {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  padding-left: 0px !important;
  text-align: left !important;
  text-align-last: left !important;
  display: inline-block;
  text-align: left !important;
  text-align-last: left !important;
  min-width: 0;
  max-width: 200px;
}
</style>
